// ---------------------------------------------------------------------------
// entityLabels — how an entity_type is SHOWN, as opposed to how it is stored.
// ---------------------------------------------------------------------------
//
// syngentaind's "Product" entities are its brand portfolio, and the sales team reads
// them as brands, so every page labels that type "Brand". Other orgs keep "Product",
// and drroof's types (Roof Issues, Customer Concerns) are unaffected.
//
// Display only, deliberately. `entity_type` is the value every picker sends back as a
// filter and the value the pipeline writes, so the stored string must stay "Product" —
// renaming the data would break the filters at one end and the pipeline at the other.
// Every `value=` stays raw; only the visible text goes through typeLabel().
//
// The map arrives from the org config on /api/config/org-tabs, which App.jsx already
// fetches once at startup, and is stashed here rather than threaded through props
// because nearly every page needs it and none of them need to react to it changing —
// it is fixed for the session once the user is logged in.
//
// Before that fetch resolves, typeLabel() is the identity function, so a page that
// renders early shows the stored type and then the real label. Types with no configured
// alias always pass through unchanged.

let LABELS = {};
// Lower-cased index of the same map. entity_type casing is not consistent across the
// API: /api/sentiment/* selects UPPER(e.entity_type), so the Voice of the Farmer cards
// receive "PRODUCT" while every other endpoint sends "Product". An exact-key lookup
// relabels one and not the other, which is how a page ends up saying "Brand" in its
// picker and "(PRODUCT)" on the card beside it.
let LABELS_CI = {};

/** Install the org's entity_type aliases. Called once, from App.jsx. */
export function setEntityTypeLabels(labels) {
  LABELS = (labels && typeof labels === 'object') ? labels : {};
  LABELS_CI = Object.fromEntries(
    Object.entries(LABELS).map(([k, v]) => [String(k).toLowerCase(), v]),
  );
}

/** The label to display for a stored entity_type. */
export function typeLabel(entityType) {
  if (!entityType) return entityType;
  // Returns the CONFIGURED label, not the caller's casing — "PRODUCT" becomes "Brand",
  // matching the pickers rather than shouting.
  return LABELS[entityType] || LABELS_CI[String(entityType).toLowerCase()] || entityType;
}

/**
 * The label for a qualified Market Watch token: 'Product', 'group:Fungicide' or
 * 'unclassified:Product'. The qualifier is stripped and the remainder aliased, so an
 * "Unclassified Product" column follows the same rename as the plain type.
 */
export function tokenLabel(token) {
  if (!token) return token;
  return typeLabel(token.includes(':') ? token.split(':').slice(1).join(':') : token);
}

/** True when this org renames at least one type — lets a page skip label work entirely. */
export function hasEntityTypeLabels() {
  return Object.keys(LABELS).length > 0;
}
